import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ServicesManager } from '@ohif/core';

import MeasurementItem from './MeasurementItem';
import Icon from '../Icon';
import Tooltip from '../Tooltip';
import { useSegmentationState } from '../../contextProviders';

const TOOL_GROUP_IDS = ['default', 'mpr', 'SRToolGroup', 'volume3d'];

const MeasurementTable = ({
  data,
  title,
  onClick,
  onEdit,
  onDelete,
  servicesManager,
  deleteMeasurementFromDB,
}) => {
  servicesManager = servicesManager as ServicesManager;
  const { customizationService, measurementService } = servicesManager.services;
  const { commandsManager } = servicesManager;
  const { t } = useTranslation('MeasurementTable');
  const { isLoaded: loadSeg } = useSegmentationState();

  const [isLengthActive, setIsLengthActive] = useState(false);

  const itemCustomization = customizationService.getCustomization('MeasurementItem', {
    content: MeasurementItem,
    contentProps: {},
  });
  const CustomMeasurementItem = itemCustomization.content;

  const setToolActiveNow = toolName => {
    commandsManager.runCommand('setToolActiveToolbar', {
      toolName,
      toolGroupIds: TOOL_GROUP_IDS,
    });
  };

  const setToolPassiveNow = toolName => {
    commandsManager.runCommand('setToolDisableToolbar', {
      toolName,
      toolGroupIds: TOOL_GROUP_IDS,
    });
  };

  const handleNewLengthClick = () => {
    if (!loadSeg) {
      return;
    }

    setIsLengthActive(prev => {
      const nextState = !prev;

      if (nextState) {
        setToolActiveNow('Length');
      } else {
        setToolPassiveNow('Length');
      }

      return nextState;
    });
  };

  const onMeasurementDeleteHandler = uid => {
    const measurement = measurementService.getMeasurement(uid);
    onDelete?.(uid);
    measurementService.remove(
      uid,
      {
        ...measurement,
      },
      true
    );
  };

  const unsavedCount = data.filter(item => item.isSaved === false).length;

  return (
    <div>
      <div className="mx-2 flex items-start gap-3 rounded border border-yellow-800 bg-white px-4 py-2">
        ⚠️
        <span className="text-sm text-yellow-800">
          The measurements depend on the image quality.
        </span>
      </div>

      <div className="mx-2 mt-2 rounded border border-[#b18aba] bg-white px-3 py-2 text-sm text-black">
        <div className="space-y-3">
          <div
            className="pt-0.5"
            style={{ textAlign: 'justify' }}
          >
            If it is not already loaded, click
            <div className="my-2 flex">
              <div
                className="inline-flex items-center rounded px-2.5 py-1 text-black text-sm font-semibold font-size-sm"
                style={{ backgroundColor: '#abe8ff' }}
              >
                LOAD Segmentation of morphologies
              </div>
            </div>
            <div>to enable the measurement tool.</div>
          </div>

          <div className="border-t border-[#d9bfdc]" />

          <div className="flex items-start gap-1">
            <span
              className="border-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
              style={{
                borderColor: '#b18aba',
                color: '#b18aba',
                backgroundColor: 'rgba(143, 93, 154, 0.3)',
              }}
            >
              1
            </span>
            <span className="pt-0.5">Select the desired measurement type.</span>
          </div>
          <div className="flex items-start gap-1">
            <span
              className="border-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
              style={{
                borderColor: '#b18aba',
                color: '#b18aba',
                backgroundColor: 'rgba(143, 93, 154, 0.3)',
              }}
            >
              2
            </span>
            <span className="pt-0.5">
              Click a point, drag, and release to create a measurement.
            </span>
          </div>
          <div className="flex items-start gap-1">
            <span
              className="border-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
              style={{
                borderColor: '#b18aba',
                color: '#b18aba',
                backgroundColor: 'rgba(143, 93, 154, 0.3)',
              }}
            >
              3
            </span>
            <span className="pt-0.5">
              Save measurements. Measurements are <span className="font-semibold">NOT</span>{' '}
              automatically saved.
            </span>
          </div>
        </div>
      </div>

      <div className="flex gap-2 px-2 py-2">
        <Tooltip
          content={
            isLengthActive
              ? 'Cancel measurement'
              : loadSeg
                ? 'Create a new length measurement'
                : 'Load segmentation first'
          }
          isDisabled={false}
        >
          <button
            onClick={handleNewLengthClick}
            className={`flex h-9 min-w-[130px] items-center justify-center gap-1.5 rounded px-3 py-1 text-base text-white transition-colors duration-200 ${
              loadSeg ? 'bg-black' : 'cursor-not-allowed bg-[#d9d9d9]'
            }`}
            disabled={!loadSeg}
          >
            <Icon
              name={isLengthActive ? 'close' : 'tool-length'}
              className="w-6 text-white transition-colors duration-200"
            />
            <span>{isLengthActive ? 'Cancel' : 'New Length'}</span>
          </button>
        </Tooltip>
      </div>

      <div className="mx-2 mt-2 mb-2 px-3 py-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-red-600">*</span>
          <span>Measurements not saved: {unsavedCount}</span>
        </div>
      </div>

      <div
        className="overflow-y-auto overflow-x-visible"
        style={{ maxHeight: '140px' }}
      >
        {data.length !== 0 ? (
          data.map((measurementItem, index) => (
            <CustomMeasurementItem
              key={measurementItem.uid}
              uid={measurementItem.uid}
              index={index + 1}
              label={measurementItem.label}
              isActive={measurementItem.isActive}
              displayText={measurementItem.displayText}
              isSaved={measurementItem.isSaved ?? false}
              onClick={onClick}
              onEdit={onEdit}
              onDelete={onMeasurementDeleteHandler}
              item={measurementItem}
            />
          ))
        ) : (
          <div className="group mt-2 flex cursor-default border border-transparent bg-gray-100 transition duration-300">
            <div className="flex flex-1 items-center justify-center px-2 py-1">
              <span className="text-base font-bold text-black">{t('No tracked measurements')}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

MeasurementTable.defaultProps = {
  data: [],
  onClick: () => {},
  onEdit: () => {},
};

MeasurementTable.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      uid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      displayText: PropTypes.arrayOf(PropTypes.string),
      isActive: PropTypes.bool,
    })
  ),
  onClick: PropTypes.func,
  onEdit: PropTypes.func,
};

export default MeasurementTable;
